/**
 * Live-editable system prompts (migration 0011, §11's prompt work, D210/D211) — Models page.
 *
 * Each prompt's default text ships in the build and is shown here for reference, collapsed
 * by default (`EyeToggle`, same idiom `AiReasoningDisclosure` uses) since it's long. The edit
 * field starts empty — never pre-filled with the default — because an empty field *is* "using
 * the default"; typing something and saving is what creates an override. "Use Default" clears
 * the field and saves that, in one action, rather than just resetting the textarea locally.
 *
 * The edit box is `useAutoGrow` + `.input.notes` — the same grow-to-fit textarea Task notes
 * uses, since a prompt override is exactly that: free text that might be one line or forty.
 *
 * **`PUT /api/prompts/{name}` is admin-only (D207)** — `GET` isn't, so a non-admin still
 * gets a real list here, just without the textarea or Save/Use-default row: offering a
 * control that always 403s isn't honest UI, and the 403's own error text has nowhere good
 * to render inside a `.tokens`-style card layout, reported directly as an error message
 * that scrolled off screen with nothing to anchor it. `canEdit` (`me?.is_admin`) is the one
 * flag every row checks; there's no separate per-row permission to worry about.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type Me, type PromptInfo } from "../api";
import { EyeToggle } from "./EyeToggle";
import { Check, Copy } from "./icons";
import { useAutoGrow } from "./useAutoGrow";

function PromptRow({
  prompt,
  canEdit,
  onSave,
}: {
  prompt: PromptInfo;
  canEdit: boolean;
  /** Saves, and returns the override as the server stored it (trimmed). Throws on failure. */
  onSave: (name: string, body: string) => Promise<string>;
}) {
  const [draft, setDraft] = useState(prompt.override);
  const [showingDefault, setShowingDefault] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"copied" | "selected" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textarea = useAutoGrow(draft);
  const defaultRef = useRef<HTMLPreElement | null>(null);

  const isCustomized = prompt.override !== "";

  async function save(body: string) {
    try {
      const override = await onSave(prompt.name, body);
      setDraft(override);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Copy the default prompt's text, so it can be pasted into the edit box as a starting
   * point. Same shape as `ClampedText`'s own `copy`: `navigator.clipboard` is undefined
   * outside a secure context, and this app is served over plain HTTP on the LAN, so the
   * happy path needs a real fallback rather than an optional-chained no-op — select the
   * text instead. Only reachable once the default is unfolded (the button isn't rendered
   * until then), so the node this selects is always on-screen already.
   */
  async function copyDefault() {
    try {
      if (window.isSecureContext && navigator.clipboard) {
        await navigator.clipboard.writeText(prompt.default);
        setCopyStatus("copied");
        window.setTimeout(() => setCopyStatus(null), 1500);
        return;
      }
    } catch {
      // Permission denied, or no clipboard. Fall through to selecting it.
    }

    const node = defaultRef.current;
    if (node) {
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    setCopyStatus("selected");
    window.setTimeout(() => setCopyStatus(null), 3000);
  }

  const copyLabel =
    copyStatus === "copied"
      ? "Copied"
      : copyStatus === "selected"
        ? "Selected - press Ctrl+C"
        : "Copy the default prompt";

  return (
    <div style={{ display: "grid", gap: "0.4rem" }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <strong>{prompt.label}</strong>
        {isCustomized && <span className="small muted">customized</span>}
      </div>

      {error && <p className="error">{error}</p>}

      <div className="row" style={{ alignItems: "flex-start" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
          <EyeToggle
            open={showingDefault}
            onToggle={() => setShowingDefault((v) => !v)}
            label={`the default ${prompt.label} prompt`}
          />
          {showingDefault && (
            <button
              type="button"
              className="btn ghost small"
              onClick={() => void copyDefault()}
              aria-label={copyLabel}
              title={copyLabel}
            >
              {copyStatus === "copied" ? <Check size={14} /> : <Copy size={14} />}
            </button>
          )}
        </div>
        {showingDefault && (
          <pre ref={defaultRef} className="muted small" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
            {prompt.default}
          </pre>
        )}
      </div>

      {canEdit && (
        <>
          <textarea
            ref={textarea}
            className="input notes"
            aria-label={`${prompt.label} override`}
            placeholder="Using the default - type here to override it"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />

          <div className="row wrap">
            <button className="btn primary small" type="button" onClick={() => void save(draft)}>
              Save
            </button>
            <button
              className="btn small"
              type="button"
              onClick={() => void save("")}
              disabled={!isCustomized && draft === ""}
            >
              Use default
            </button>
            {saved && (
              <span className="small" style={{ color: "var(--good)" }}>
                Saved
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function PromptsCard({
  refreshKey,
  onChange,
}: {
  refreshKey?: number;
  onChange?: () => void | Promise<void>;
}) {
  const [prompts, setPrompts] = useState<PromptInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  const load = useCallback(async () => {
    try {
      const { prompts: list } = await api.prompts();
      setPrompts(list);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return;
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    void api.me().then(setMe);
  }, []);

  const canEdit = me?.is_admin ?? false;

  const save = useCallback(
    async (name: string, body: string) => {
      const result = await api.setPromptOverride(name, body);
      setPrompts((list) =>
        list.map((p) => (p.name === name ? { ...p, override: result.override } : p)),
      );
      await onChange?.();
      return result.override;
    },
    [onChange],
  );

  return (
    <section className="panel">
      <header>
        <h2>Prompts</h2>
        <span className="muted small">
          {canEdit
            ? "Tweak the wording the AI is given, live - no rebuild or restart"
            : "The wording the AI is given for each kind of call - the owner's to tweak"}
        </span>
      </header>

      {error && <p className="error">{error}</p>}

      <div style={{ display: "grid", gap: "1rem" }}>
        {prompts.map((prompt) => (
          <PromptRow key={prompt.name} prompt={prompt} canEdit={canEdit} onSave={save} />
        ))}
      </div>
    </section>
  );
}
